// Utility to get site settings from localStorage (with database sync on page load)
import apiService from '../services/apiService';

// ── Legal page default content (Markdown, fully admin-editable) ──────────
// Rendered with react-markdown on /privacy and /terms. Editable in
// Admin → Customise → "Privacy Policy Page" / "Terms of Service Page".
const TERMS_DOC_BODY_DEFAULT = `## 0. Who we are

AI Doctor Ben is operated by Qudos Hub Ltd, a company registered in England and Wales with company number 16784632 and registered office at 20 Wenlock Road, London, N1 7GU, United Kingdom.

## 1. Agreement to Terms

By accessing or using AI Doctor Ben (the "Service"), you agree to be bound by these Terms of Service. If you do not agree, please do not use our Service.

The Service may be accessed by users in different countries. Certain features, subscriptions, or offers may not be available in all locations, and some rights described in these Terms may apply differently depending on where you live.

## 2. Service Description

AI Doctor Ben provides AI-powered health information analysis. Our Service includes:

- Health question analysis from multiple medical perspectives
- Document analysis (OCR and AI interpretation)
- Secure, device-isolated health record storage
- Health tracking and timeline features

## 3. Medical Disclaimer

**IMPORTANT: AI Doctor Ben is NOT a medical service and does NOT provide medical advice, diagnosis, or treatment. The information provided is for educational medical insights and general informational purposes only.**

- Always consult qualified healthcare professionals for medical decisions
- Do not delay seeking medical care based on information from this Service
- In case of emergency, contact emergency services immediately
- AI-generated content may contain errors or inaccuracies

The Service does not replace consultation with a doctor or other qualified healthcare professional in any jurisdiction.

The Service may generate personalised insights based on your inputs and health records, but these insights are informational only and are not a substitute for advice from a doctor or other qualified healthcare professional. They may not take account of all of your medical history, medications, or risk factors, and may not be appropriate for your specific circumstances or location.

## 4. User Responsibilities

You agree to:

- Provide accurate account information
- Maintain the security of your account credentials
- Use the Service only for lawful purposes
- Not attempt to circumvent security measures
- Not share your account with others
- Be at least 18 years old to use the Service

## 5. Subscription and Payments

### 5.1 Subscription Plans

Access to certain features requires a paid subscription. Subscription fees are charged in advance on a recurring basis.

### 5.2 Billing

Subscriptions automatically renew unless cancelled. You may cancel at any time through your account settings.

### 5.3 Refunds

Refund requests are handled on a case-by-case basis. Contact support within 14 days of purchase for refund consideration.

## 6. Intellectual Property

The Service, including its design, features, and content, is owned by AI Doctor Ben and protected by intellectual property laws.

You retain ownership of your health data and documents. By using the Service, you grant us a limited license to process your queries for the purpose of providing the Service.

## 7. Privacy

Your use of the Service is also governed by our [Privacy Policy](/privacy). Please review it to understand how we collect and use your information.

## 8. Limitation of Liability

**To the maximum extent permitted by law:**

- The Service is provided "AS IS" without warranties of any kind
- We are not liable for any health decisions made based on Service information
- We are not liable for indirect, incidental, or consequential damages

Our total liability for all claims arising out of or in connection with the Service shall not exceed the total subscription fees paid by you to us in the 12 months immediately before the event giving rise to the claim.

## 9. Termination

We may suspend or terminate your access to the Service at our discretion, including for violation of these Terms. Upon termination, your right to use the Service ceases immediately. Your locally stored health data remains on your device.

## 10. Changes to Terms

These Terms may be updated periodically as we introduce new features and services. We will notify you of material changes via email or in-app notification. Your continued use of AI Doctor Ben after changes are posted constitutes acceptance of the updated Terms.

We encourage you to review these Terms periodically. The "Last Updated" date at the top of this page indicates when the Terms were most recently revised.

## 11. Governing Law and where you may bring claims

These Terms and any dispute or claim (including non-contractual disputes or claims) arising out of or in connection with them are governed by the laws of England and Wales.

If you live in the UK or EEA, you may also be entitled to bring claims in the courts of your country of residence under mandatory local consumer-protection law. Nothing in these Terms affects any non-excludable rights you have under that law.

## 12. Contact Us

For questions about these Terms, contact us at: [support@aidoctorben.com](mailto:support@aidoctorben.com)`;


// Flag to track if we've synced from DB
let hasSyncedFromDB = false;
let syncPromise = null;

// Sync settings from database (called once on app load)
export const syncSettingsFromDatabase = async () => {
  if (syncPromise) return syncPromise;
  
  syncPromise = (async () => {
    try {
      const dbSettings = (await apiService.getGeneralSettings())?.settings;
      if (dbSettings && Object.keys(dbSettings).length > 0) {
        // Get current local settings
        const localSettings = getSiteSettings();
        // Merge: DB settings take precedence
        const merged = { ...localSettings, ...dbSettings };
        // Save merged settings to localStorage
        localStorage.setItem('site_settings', JSON.stringify(merged));
        hasSyncedFromDB = true;
        console.log('Settings synced from database');
        return merged;
      }
    } catch (error) {
      console.log('Could not sync settings from database:', error.message);
    }
    hasSyncedFromDB = true;
    return getSiteSettings();
  })();
  
  return syncPromise;
};

export const getSiteSettings = () => {
  const SETTINGS_VERSION = '2.9'; // Increment this when adding new fields
  
  // Default settings with current blue theme
  const defaults = {
    version: SETTINGS_VERSION,
    // General Branding
    siteName: 'AI Doctor Ben',
    siteTagline: 'Your Private Health Data Assistant',
    logo: '🏥🤖',
    logoUrl: '',
    
    // Personalization - Welcome Message
    welcomeNamePrompt: 'How do you prefer to be addressed?',
    welcomeIntroText: 'Hi! I\'m Dr. Ben, your AI health specialist.',
    welcomeDescriptionText: 'I analyze health questions from 12+ medical perspectives, all kept private on your device.',
    
    // Warm Mode Colors & Theme (Advanced)
    // These feed into [data-theme="light"] via index.css. Dark mode is unaffected.
    // Defaults are Schedule A (warm cream + coral). Use "Reset to Warm Defaults" in
    // admin to restore these values if you've experimentally tweaked them.
    primaryColor: '#E05A47',      // Coral - CTA gradient start, accent, links
    secondaryColor: '#8B3A62',    // Magenta - CTA gradient end
    backgroundStart: '#FBF9F6',   // Warm cream - page background
    backgroundEnd: '#FBF9F6',     // (unused in warm mode — page bg is solid cream)
    cardBackground: '#FFFFFF',    // White - card surfaces
    borderColor: '#E8E2D9',       // Soft sand - borders
    
    // NEW Landing Page (Fast Engagement) - Colors
    landingBgStart: '#f8fafc',    // Slate 50 - gradient start
    landingBgEnd: '#eff6ff',      // Blue 50 - gradient end
    landingCardBg: '#ffffff',     // White - cards
    landingTextPrimary: '#0f172a', // Slate 900 - headings
    landingTextSecondary: '#475569', // Slate 600 - body text
    
    // NEW Landing Page - Hero Text
    landingHeroBadge: 'Early Access · Educational only. Not medical advice.',
    landingHeroTitle: 'See the full story in your blood tests over time – privately, on your device.',
    // "How it works & stays private" inline accordion (Apr 28, 2026)
    // Default ON. Trigger empty → hidden. Body supports **bold**, *italic*, \n paragraphs.
    landingHowItWorksEnabled: 'true',
    landingHowItWorksTrigger: 'How it works & stays private',
    landingHowItWorksContent: 'How AI Doctor Ben works\n\nUpload blood tests, ECGs, scan results, or any reports from all your health professionals.\n\nAI Doctor Ben helps organise your records, compare multiple educational health viewpoints, and track changes over time.\n\nUse it to have clarity from results now for you and your family and even prepare smarter questions for your doctor.\n\nEducational insights, [governed by EveR Local Layer](/about#privacy). Not currently a medical service.',
    landingHeroSubtitle: 'Store records on your device, chat with a panel of AI\'s across conventional and holistic viewpoints, and track trends so you can ask better questions with your doctor.',
    landingHeroSubheading: 'From GP to dentist to homeopath – 12 perspectives on one private timeline.',
    landingBelowHeroPrivacy: 'Your health records stay on your device. Questions are processed briefly by selected AI providers, then discarded – no long-term record of your health data on their servers.',
    landingBelowHeroBeta: '$16/month locks in forever.',
    landingBelowHeroBetaUS: '$16/month locks in forever.',
    landingBelowHeroBetaUK: '£13/month locks in forever.',
    landingBelowHeroBetaEU: '€12/month locks in forever.',
    landingBelowHeroBetaCZ: '200 Kč/month locks in forever.',
    landingBelowHeroBetaJP: '¥1,500/month locks in forever.',
    landingBelowHeroBetaDK: '69 kr/month locks in forever.',
    landingBelowHeroBetaKZ: '₸4,000/month locks in forever.',
    
    // NEW Landing Page - Input Labels
    landingQuestionLabel: 'Your Health Question',
    landingQuestionPlaceholder: 'Describe your health concern or question... (e.g., \'What do my cholesterol levels mean?\' or \'I\'ve been experiencing too much fatigue lately...\')',
    landingQuestionPlaceholderRotation: '',
    landingUploadLabel: 'Upload Health Documents (Optional)',
    landingUploadText: 'Drag & drop files here, or click to browse',
    landingUploadHint: 'PDFs, JPGs, PNGs • Up to 10 files at a time • 10MB each',
    
    // NEW Landing Page - Button
    landingButtonText: 'Give me insights',
    landingButtonLoadingText: 'Assessing....',

    // Landing Page - EveR LL Trust Badge (shown above Analyze button)
    landingTrustBadgeText: 'EveR LL Governed',
    landingTrustBadgeSubtext: 'Private by design',

    // Landing Page - Guided Health Questions pilot block (after features, before FAQ)
    guidedPilotLabel: 'Governed beta pilot',
    guidedPilotTitle: 'Guided Health Questions',
    guidedPilotBody: 'We are preparing a small opt-in beta for selected members. The first planned corridor is HbA1c pattern exploration: helping people compare educational paths, uncertainties, trade-offs, and clinician questions in a controlled way.',
    guidedPilotSmallLine: 'Educational guidance only. Not diagnosis, treatment, triage, or medical instruction.',
    guidedPilotButtonText: 'Learn about Guided Health Questions',
    guidedPilotButtonLink: '/about',
    
    // NEW Landing Page - Feature Cards
    landingFeature1Icon: '⚡',
    landingFeature1Title: 'Health Insights',
    landingFeature1Desc: 'Get our AI-powered view of your health questions from our panel of AI with multiple points of view. Check out the Timeline feature; Compare and contrast, get insights from your ECG, cholesterol, hormones, supplements and more over months and years.',
    landingFeature2Icon: '🌿',
    landingFeature2Title: 'Health Specialities',
    landingFeature2Desc: 'Conventional medic, integrative, functional, homeopathic, naturopathy, and 7+ other perspectives all in one place.',
    landingFeature3Icon: '📊',
    landingFeature3Title: 'Document Assessment',
    landingFeature3Desc: 'Upload health tracker results, blood tests, lab results, or medical reports, even x-rays. Our multi-AI extracts the data. Records stay on your device - we can\'t see or sell your data (private by design).',
    
    // NEW Landing Page - Trust Indicators
    landingTrust1: 'Device-Isolated Storage',
    landingTrust2: 'Privacy-First Security',
    landingTrust3: 'Health Records On-Device',
    
    // NEW Landing Page - Sub-Button Copy (Two Lines with icons)
    landingSubButtonCopyVisible: 'false',
    landingCtaPrivacyLine: '🔒 Your health data is redacted before AI processing. Your health records stay on your device after you subscribe.',
    landingSubButtonIcon1: '',
    landingPrivacyNote: 'We are opening AI Doctor Ben slowly while we learn from early Users. 50 spots → lock £13/month forever',
    landingPrivacyNoteUS: 'We are opening AI Doctor Ben slowly while we learn from early Users. 50 spots → lock $16/month forever',
    landingPrivacyNoteUK: 'We are opening AI Doctor Ben slowly while we learn from early Users. 50 spots → lock £13/month forever',
    landingPrivacyNoteEU: 'We are opening AI Doctor Ben slowly while we learn from early Users. 50 spots → lock €12/month forever',
    landingPrivacyNoteCZ: 'We are opening AI Doctor Ben slowly while we learn from early Users. 50 spots → lock 200 Kč/month forever',
    landingPrivacyNoteJP: 'We are opening AI Doctor Ben slowly while we learn from early Users. 50 spots → lock ¥1,500/month forever.',
    landingPrivacyNoteDK: 'We are opening AI Doctor Ben slowly while we learn from early Users. 50 spots → lock 69 kr/month forever',
    landingPrivacyNoteKZ: 'We are opening AI Doctor Ben slowly while we learn from early Users. 50 spots → lock ₸4,000/month forever',
    landingSubButtonIcon2: '🔒',
    landingPrivacyNote2: 'Your health data is processed securely. Health records are stored only on your device after you subscribe.',
    
    // Vision Strip (authority/foresight section below CTA)
    visionStripTitle: 'From letters on horseback to WhatsApp \u2013 health will change just as fast.',
    visionStripBody: 'Medicine has always advanced when we can see more of the picture\u2014X\u2011rays, MRI, DNA. AI is the next jump: it will eventually spot patterns across a lifetime of data faster than any one clinician, while humans stay essential for judgement, values, and care.\n\nAI Doctor Ben is built for that future, but today it\u2019s simply a private health assistant that helps you organise your records, understand lab results, and walk into appointments better prepared\u2014not a doctor and not a replacement for one.',
    
    // Trust Line (between Privacy Line and Beta Line — supports **bold** and *italic* markdown)
    landingTrustLine: '',

    // Paywall Modal
    paywallSuccessTitle: 'Analysis Complete!',
    paywallSuccessSubtitle: 'Your health insights are ready',
    paywallPreviewTitle: 'Your Analysis Contains:',
    paywallMetricsLabel: 'Health Metrics',
    paywallFindingsLabel: 'Key Findings',
    paywallRecommendationsLabel: 'Recommendations',
    paywallBlurText: 'Your cholesterol levels show... HDL at 58 mg/dL which is considered... recommend lifestyle modifications including...',
    paywallLockText: 'Subscribe to unlock',
    paywallButtonText: 'Subscribe to View Results →',
    paywallLoginText: 'Already have an account? Login',
    paywallPrivacyNote: 'Your analysis is saved locally. It will be available after you subscribe.',
    
    // DISCOVER Page (old landing) - Hero
    heroTitle: 'Your Personal Health Data Portal',
    heroSubtitle: 'Store records on your device, chat with a panel of AI\'s across conventional and holistic viewpoints, and track trends so you can ask better questions with your doctor.',
    
    // DISCOVER Page - Feature Cards (6 cards with icons)
    feature1Icon: '🔒',
    feature1Title: 'Device-Isolated Records',
    feature1Description: 'Your health records (documents, analysis, conversations) stay on your device. Personalisation data is stored server-side with your consent to power AI responses.',
    
    feature2Icon: '🤖',
    feature2Title: 'Smart AI Analysis',
    feature2Description: 'Your questions are answered by carefully selected AI models for focused, educational health insights.',
    
    feature3Icon: '🌿',
    feature3Title: 'Holistic Perspectives',
    feature3Description: 'Get insights from conventional, integrative, Ayurveda, TCM, naturopathy, and 7+ other medical perspectives.',
    
    feature4Icon: '📄',
    feature4Title: 'OCR Extraction',
    feature4Description: 'Upload PDFs or images of your health reports. AI extracts text automatically for analysis.',
    
    feature5Icon: '💬',
    feature5Title: 'Conversational Interface',
    feature5Description: 'Ask questions naturally. "What\'s my cholesterol trend?" "Analyze my liver enzymes from multiple perspectives."',
    
    feature6Icon: '🛡️',
    feature6Title: 'Privacy-First Security',
    feature6Description: 'Health records stay on your device. Server-side data is protected by access controls and encryption in transit. We store only what is needed.',
    
    // DISCOVER Page - Bottom CTA
    ctaTitle: 'Ready to secure your health data in your own private vault?',
    ctaButton: 'Create Your Health Vault',
    ctaSubtitle: 'Privacy-first. Health records on your device. Personalisation with consent.',
    
    // Landing Page - Bottom Disclaimer
    disclaimer: 'This tool provides information for educational purposes only. This is NOT medical advice. Always consult qualified healthcare professionals for medical decisions.',
    
    // Medical Disclaimer (3 fields)
    disclaimerTitle: 'Important Medical Disclaimer',
    disclaimerMain: 'This AI-enabled Health Support site is a Space for you that enables effective organization of your health discussions and analyses.',
    disclaimerWarning: 'Seek medical assessment for any concerns. This interaction is for educational purposes only.',
    
    // About Page - Main Section (matches live /about page)
    aboutTitle: 'What is AI Doctor Ben?',
    aboutIntro: 'that provides comprehensive medical insights from 12+ medical perspectives. It analyzes your symptoms, lab results, and medical documents to deliver holistic health guidance, helping you make more informed decisions about your health journey.',

    // About Page - 12 Perspectives subheading, CTA block & footer line
    perspectivesSubheading: 'The 12+ Medical Perspectives:',
    aboutCtaTitle: 'Ready to understand your health better?',
    aboutCtaSubtext: 'Get AI-powered insights from 12+ medical perspectives.',
    aboutCtaPrimaryBtn: 'View Pricing',
    aboutCtaSecondaryBtn: 'Try Free Analysis',
    aboutFooterText1: 'Have more questions? Check our',
    aboutFooterFaqLabel: 'FAQ page',
    aboutFooterText2: 'or read our',
    aboutFooterPrivacyLabel: 'Privacy Policy',
    
    // About Page - AI Vision Section
    visionTitle: 'Our AI Health Vision',
    visionSubhead: 'Built to support real doctors in person, not to replace them.',
    visionContent: 'AI for health should be viewed as an enhancement rather than a replacement. Human doctors bring empathy, context, intuition, and patient-specific judgment; qualities no machine can fully replicate. We see AI Doctor Ben\'s role as to streamline complex analysis with fast well thought through answers presented as an interactive AI consult and not to eliminate the expertise behind them. We are leaving real medical decisions in the hands of qualified professionals and have a vision to encourage collaboration with clinics internationally.',
    
    // Landing Page - Vision One-Liner
    visionOneLiner: 'AI that supports doctors, not replaces them',
    
    // About Page - How It Works
    howItWorksTitle: 'How AI Doctor Ben Works',
    howItWorksThread: 'Organize health discussions by topic, medications, symptoms, conditions, in separate threads.',
    howItWorksMulti: 'Every health query analyzed from 12+ medical viewpoints simultaneously.',
    howItWorksAction: 'Get personalized recommendations based on your health data patterns.',
    howItWorksTrend: 'Visual charts showing how your health markers change over time.',
    // How It Works — now fully editable (intro, features heading, per-point icon+title, +5th point). Blank a point Title to hide it.
    howItWorksIntro: '**Upload lab results over time and AI Doctor Ben automatically builds trend charts.** The system extracts health metrics from your PDFs and images, organizing them chronologically.',
    howItWorksFeaturesHeading: 'Timeline Features:',
    howItWorksThreadIcon: '👥',
    howItWorksThreadTitle: 'Thread-Based Organization',
    howItWorksMultiIcon: '🎯',
    howItWorksMultiTitle: 'Multi-Perspective Analysis',
    howItWorksTrendIcon: '📈',
    howItWorksTrendTitle: 'Trend Monitoring',
    howItWorksActionIcon: '🎯',
    howItWorksActionTitle: 'Actionable Insights',
    howItWorksStoryIcon: '📖',
    howItWorksStoryTitle: 'Stay with the story',
    howItWorksStory: 'Build a record over months and years, not just for one rushed question.',
    
    // NEW About sections (v0.1, Jun 2026) — editable via Admin/Customize → About. Blank a Title to hide that section.
    aboutHisHersTitle: 'The wider direction: His & Hers',
    aboutHisHersBody: 'AI Doctor Ben is the first partner in a wider Whater.org direction called His & Hers: a long-term framework for private family health intelligence, collaboration, and future research into health patterns.\n\nThe first promise is personal: help people and families understand their own information more clearly. The longer-term possibility is larger: with consent, protected patterns may one day support better awareness, earlier questions, and new forms of health research.',
    aboutHowWorksTitle: 'How AI Doctor Ben Works',
    aboutHowWorksItem1Title: 'Keep your records together',
    aboutHowWorksItem1Body: 'Upload lab results, letters, scans, tracker reports, and older documents into a private health workspace, so the story is easier to follow over time.',
    aboutHowWorksItem2Title: 'See what changed',
    aboutHowWorksItem2Body: 'AI Doctor Ben helps organise results chronologically and show how markers have moved, so one result is not left floating on its own.',
    aboutHowWorksItem3Title: 'Compare educational perspectives',
    aboutHowWorksItem3Body: 'Different health frameworks may ask different questions of the same information. AI Doctor Ben helps you compare those perspectives without forcing one answer.',
    aboutHowWorksItem4Title: 'Prepare better questions',
    aboutHowWorksItem4Body: 'Turn scattered records and confusing results into clearer questions for your next clinician conversation.',
    aboutHowWorksItem5Title: 'Stay with the story',
    aboutHowWorksItem5Body: 'Build a record over months and years, not just for one rushed question.',
    aboutGovernedTitle: 'The next step: governed guidance',
    aboutGovernedBody: 'AI Doctor Ben begins with education, organisation, multiple perspectives, and better questions. The next step is a small governed beta called Guided Health Questions.\n\nThe first planned corridor is HbA1c pattern exploration. The aim is to test whether AI can help selected members compare educational paths, uncertainties, trade-offs, and clinician questions in a controlled way.\n\nThis is where stronger guidance starts carefully: named use case, opt-in members, logged outputs, reviewed boundaries, and decisions that remain with the member and clinician.',
    aboutBuildTitle: 'Build the record before the moment you need it',
    aboutBuildBody: 'AI Doctor Ben is only at the beginning, but the direction is clear: private records that become easier to understand, educational perspectives that make better questions possible, and a governed path toward more useful support over time.\n\nStart with the records you already have. Grow the picture from there.',
    aboutBuildCta: 'Start with your records.',

    // About Page - Privacy
    privacyTitle: 'Privacy & Security Architecture',
    // Privacy feature icons (editable — blank to hide an icon)
    privacyDeviceStorageIcon: '✅',
    privacyNoSyncIcon: '✅',
    privacyAnonymousIcon: '✅',
    privacyZeroTrustIcon: '✅',
    privacyRoutingIcon: '✅',
    privacyEverLLIcon: '✅',
    privacyMultiDeviceLabel: 'Important:',
    privacyDeviceStorage: 'Each device maintains a completely separate, private health vault. No data sharing between devices.',
    privacyNoSync: 'Your health data on one device cannot be accessed from another. Complete isolation by design.',
    privacyAnonymous: 'Your stored records stay on your device. Only the text you choose to include is sent, with identifying details removed.',
    privacyZeroTrust: 'Even if our server is compromised, your health data remains secure on your device.',
    privacyRouting: 'Your questions are answered by carefully selected AI models.',
    privacyEverLLTitle: 'EveR LL Governance',
    privacyEverLL: 'EveR is our local governance layer that keeps responses educational and private.',
    privacyEverLLLink: '',
    privacyMultiDevice: 'If you want to access your health data from multiple devices, we provide auto email/download of all data and chats. It is a one button click to upload your protected data onto a separate account for each device. This is intentional to maximize privacy.',
    
    // ========== NEW: Single-Device Session Security Messaging ==========
    // Session Conflict Modal (when trying to login while active elsewhere)
    sessionConflictTitle: 'Device-Locked Session Active',
    sessionConflictMessage: 'Your account is currently active on another device. For your privacy and the security of your health records, only ONE device can access your account at a time.',
    sessionConflictWhyTitle: 'Why single-device access?',
    sessionConflictReason1: 'Your health records only exist on this one device',
    sessionConflictReason2: 'If you login on another device, you will have no records there',
    sessionConflictReason3: 'This protects your health records from being accessed elsewhere',
    sessionConflictForceButton: 'Force Login on This Device',
    sessionConflictForceWarning: 'Force login will end your other session. Your health records remain on the original device.',
    sessionConflictCancelButton: 'Cancel',
    
    // Dashboard Privacy Badge
    privacyBadgeTitle: 'Device-Isolated Vault Active',
    privacyBadgeMessage: 'Your health records only exist on this one device. If you login on another device, you will have no records there. This provides maximum protection for your health being valued.',
    
    // Login Page Privacy Box
    loginPrivacyTitle: 'Ultra-Private, Single-Device Health Vault',
    loginPrivacyMessage: 'Your account and health records are stored on THIS device only. Health records never leave your browser. Each device requires its own login for maximum privacy.',
    loginDisclaimer: 'Not medical advice. For educational purposes only.',
    
    // Timeline Content
    timelineTagline: 'Probably the best Private Health Timeline in the world. Built by you for your future.',
    
    // Beta Access Gate (for pricing page)
    betaGateEnabled: false,  // OFF by default — gates removed for conversion
    betaWelcomeEnabled: false,  // OFF by default — home page welcome modal removed
    betaAccessCode: 'EARLYBEN2026',  // Shared code for Discord users
    betaButtonText: '🔐 Have a Beta Code? Get Access',  // Home page beta button text
    betaEmailDelayHours: 2,  // Hours delay before sending code email
    betaSpotsRemaining: 46,  // Creates urgency - update weekly
    betaEmailSubject: "🔥 You're in—beta code: {code}",
    betaEmailBody: `the.aidoctorben.com/pricing → enter code → £15/mo locked forever

Track cholesterol/testosterone across 5 AI perspectives. Upload first lab in 5 mins.

{spots} spots left. Know 2 biohackers who'd benefit? 🚀`,
    betaShareMessage: `🔥 Just got early access to AI Doctor Ben - 5 AI perspectives on your health data, all private on your device.

Get your code: the.aidoctorben.com/pricing

Only 46 spots left at £15/mo!`,
    
    // Logged Out Messages
    loggedOutAnotherDeviceMessage: 'You were logged out because your account was accessed from another device. Only one active session is allowed.',
    loggedOutExpiredMessage: 'Your session has expired. Please login again.',

    // ========== Pricing Page — "Why people choose AI Doctor Ben" trust block ==========
    // (Apr 30, 2026 PM — extracted from hardcoded JSX so admin can edit/blank.)
    // Per §29 Progressive Disclosure: blank heading hides whole section. Empty
    // features list / pills list hides those sub-blocks.
    pricingWhyChooseHeading: 'Why people choose AI Doctor Ben',
    pricingWhyChooseFeatures: [
      { icon: '🔒', text: 'Keep health records private and in your control' },
      { icon: '🔬', text: 'Understand blood tests and results more clearly' },
      { icon: '👥', text: 'Compare multiple health perspectives before appointments' },
      { icon: '📊', text: 'Track markers over time in one place' },
      { icon: '💬', text: 'Ask better questions with more confidence' },
    ],
    pricingWhyChooseUseCases: [
      'Just received blood test results',
      "Managing a parent or partner's health",
      'Wanting private trend tracking over time',
    ],
    
    // ========== Navigation Button Labels ==========
    navAboutButtonText: 'About',
    navFeaturesButtonText: 'Features',
    navBackToDashboardText: 'Back to Dashboard',
    
    // ========== Health Page (/health) ==========
    healthPageSiteName: 'AI Doctor Ben',
    healthPageTagline: 'Private AI insight for confusing lab results',
    healthPageHeroTitle: 'Clear health insights,\ngrounded in science.',
    healthPageHeroSubtitle: 'Short guides that help you understand real lab results, use AI safely, and keep your health data in a private vault on your device — educational only, not medical advice.',
    healthPageCtaText: 'Explore health guides',
    healthPageArticlesIntro: '',

    // ========== Company/Contact Info ==========
    companyName: 'AI Doctor Ben',
    contactEmail: 'support@aidoctorben.com',
    privacyLastUpdated: 'April 10, 2026',
    termsLastUpdated: 'December 10, 2025',

    // ========== Legal Pages — fully editable Markdown documents ==========
    // /privacy and /terms render these via react-markdown. Title + Last
    // Updated + full Markdown body are all admin-editable.
    privacyDocTitle: '',
    privacyDocLastUpdated: '',
    privacyDocBody: '',
    termsDocTitle: 'Terms of Service',
    termsDocLastUpdated: 'May 4 2026',
    termsDocBody: TERMS_DOC_BODY_DEFAULT
  };
  
  // Merge stored settings with defaults to ensure all fields exist
  const stored = localStorage.getItem('site_settings');
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      
      // Check if stored version matches current version
      // If not, use defaults for new fields but keep old customizations
      if (parsed.version !== SETTINGS_VERSION) {
        console.log('Settings version mismatch, merging with new defaults');
        // Merge: defaults first, then override with any stored values
        const merged = { ...defaults, ...parsed, version: SETTINGS_VERSION };
        // Save the merged version
        localStorage.setItem('site_settings', JSON.stringify(merged));
        return merged;
      }
      
      // Merge defaults with stored values (stored values take precedence)
      return { ...defaults, ...parsed };
    } catch (e) {
      console.error('Failed to parse stored settings:', e);
      return defaults;
    }
  }
  
  return defaults;
};
